/**
 * SEO helpers — schema.org JSON-LD injection for BusinessDetail + SEO pages.
 * Google Rich Snippets: LocalBusiness, BreadcrumbList, ItemList, AggregateRating, Review.
 */

const SITE = "https://www.moncommerce68.fr";

function safeWindowUrl() {
  if (typeof window === "undefined") return undefined;
  return window.location.href;
}

function injectJsonLd(id, data) {
  let ld = document.getElementById(id);
  if (!ld) {
    ld = document.createElement("script");
    ld.id = id;
    ld.type = "application/ld+json";
    document.head.appendChild(ld);
  }
  // Strip undefined deeply
  const clean = JSON.parse(JSON.stringify(data, (k, v) => (v === undefined || v === null ? undefined : v)));
  ld.textContent = JSON.stringify(clean);
}

export function removeJsonLd(id) {
  const ld = document.getElementById(id);
  if (ld) ld.remove();
}

const DAY_TO_SCHEMA = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

function buildOpeningHoursSpec(hours) {
  if (!hours || typeof hours !== "object") return undefined;
  const specs = [];
  for (const [day, val] of Object.entries(hours)) {
    const schemaDay = DAY_TO_SCHEMA[day];
    if (!schemaDay || !val || val === "closed") continue;
    // Supports the 2-range format "09:00-12:00,14:00-19:00" — emit one
    // OpeningHoursSpecification per contiguous range (Google Rich Results
    // recommendation for shops that close for lunch).
    const parts = String(val).split(",");
    for (const p of parts) {
      const [o, c] = p.split("-");
      if (!o || !c) continue;
      specs.push({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: schemaDay,
        opens: o,
        closes: c,
      });
    }
  }
  return specs.length ? specs : undefined;
}

/** Inject Business (LocalBusiness) + BreadcrumbList + Review array. */
export function injectBusinessSchema(biz) {
  if (!biz) return;
  const url = safeWindowUrl();
  const type = biz.category?.toLowerCase().includes("restaur") ? "Restaurant"
    : biz.category?.toLowerCase().includes("boulang") ? "Bakery"
    : biz.category?.toLowerCase().includes("coiff") ? "HairSalon"
    : biz.category?.toLowerCase().includes("garage") ? "AutoRepair"
    : biz.category?.toLowerCase().includes("hotel") || biz.category?.toLowerCase().includes("hôtel") ? "Hotel"
    : "LocalBusiness";

  const business = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": url,
    name: biz.name,
    description: biz.description?.fr,
    image: (biz.photos && biz.photos[0]) || biz.logo || undefined,
    logo: biz.logo || undefined,
    address: {
      "@type": "PostalAddress",
      streetAddress: biz.address,
      addressLocality: biz.city,
      postalCode: biz.postal_code,
      addressRegion: "Grand Est",
      addressCountry: "FR",
    },
    areaServed: biz.city ? { "@type": "City", name: biz.city } : undefined,
    geo: biz.lat && biz.lon ? { "@type": "GeoCoordinates", latitude: biz.lat, longitude: biz.lon } : undefined,
    telephone: biz.phone || undefined,
    url,
    sameAs: [biz.website].filter(Boolean),
    priceRange: biz.price_range || "€€",
    aggregateRating: biz.rating_count > 0 ? {
      "@type": "AggregateRating",
      ratingValue: biz.rating_avg,
      reviewCount: biz.rating_count,
      bestRating: 5,
      worstRating: 1,
    } : undefined,
    review: (biz.reviews || []).slice(0, 5).map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author_name || "Client" },
      datePublished: r.created_at,
      reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      reviewBody: r.text,
    })),
    openingHoursSpecification: buildOpeningHoursSpec(biz.hours),
  };
  injectJsonLd("mc68-jsonld", business);

  // Breadcrumb
  const city = biz.city;
  const crumbs = [
    { name: "Accueil", item: `${SITE}/` },
    { name: "Annuaire", item: `${SITE}/annuaire` },
  ];
  if (city) crumbs.push({ name: city, item: `${SITE}/villes/${slugify(city)}` });
  crumbs.push({ name: biz.name, item: url });
  injectJsonLd("mc68-breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: c.item,
    })),
  });
}

/** Inject ItemList of businesses + BreadcrumbList for city/category/city+category pages. */
export function injectListSchema({ items, pageTitle, pageDescription, crumbs }) {
  if (!items || items.length === 0) return;
  const url = safeWindowUrl();
  injectJsonLd("mc68-itemlist", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: pageTitle,
    description: pageDescription,
    url,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 25).map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/entreprise/${b.slug}`,
      item: {
        "@type": "LocalBusiness",
        name: b.name,
        address: {
          "@type": "PostalAddress",
          addressLocality: b.city,
          postalCode: b.postal_code,
          addressCountry: "FR",
        },
        aggregateRating: b.rating_count > 0 ? {
          "@type": "AggregateRating",
          ratingValue: b.rating_avg,
          reviewCount: b.rating_count,
        } : undefined,
        url: `${SITE}/entreprise/${b.slug}`,
      },
    })),
  });

  if (crumbs && crumbs.length > 0) {
    injectJsonLd("mc68-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: c.name,
        item: c.item,
      })),
    });
  }
}

function injectMeta(name, content, attr = "name") {
  if (!content) return;
  let m = document.querySelector(`meta[${attr}="${name}"]`);
  if (!m) { m = document.createElement("meta"); m.setAttribute(attr, name); document.head.appendChild(m); }
  m.setAttribute("content", content);
}

function injectCanonical(href) {
  if (!href) return;
  let l = document.querySelector('link[rel="canonical"]');
  if (!l) { l = document.createElement("link"); l.setAttribute("rel", "canonical"); document.head.appendChild(l); }
  l.setAttribute("href", href);
}

/** Extract keyword tokens from business name + category + city for meta keywords & alt SEO. */
function buildKeywords(biz) {
  const tokens = new Set();
  (biz.name || "").toLowerCase().split(/[\s\-,./]+/).filter((t) => t.length > 2).forEach((t) => tokens.add(t));
  (biz.category || "").toLowerCase().split(/[\s\-,./]+/).filter((t) => t.length > 3).forEach((t) => tokens.add(t));
  if (biz.city) tokens.add(biz.city.toLowerCase());
  if (biz.postal_code) tokens.add(biz.postal_code);
  tokens.add("haut-rhin");
  tokens.add("68");
  tokens.add("alsace");
  if (biz.name) tokens.add(biz.name.toLowerCase());
  if (biz.city && biz.category) tokens.add(`${biz.category} ${biz.city}`.toLowerCase());
  return Array.from(tokens).slice(0, 20).join(", ");
}

/** Build an SEO-optimized title: business name + category + city + postal + brand. */
export function buildBusinessTitle(biz) {
  const parts = [biz.name];
  if (biz.category) parts.push(biz.category);
  if (biz.city) parts.push(`${biz.city}${biz.postal_code ? ` (${biz.postal_code})` : ""}`);
  return `${parts.join(" — ")} | Mon Commerce 68`;
}

/** Build an SEO-optimized meta description (150–160 chars, keyword-dense). */
export function buildBusinessDescription(biz) {
  if (biz.description?.fr && biz.description.fr.length > 60) return biz.description.fr.slice(0, 160);
  const parts = [biz.name];
  if (biz.category) parts.push(biz.category.toLowerCase());
  if (biz.city) parts.push(`à ${biz.city}${biz.postal_code ? ` (${biz.postal_code})` : ""}`);
  parts.push("Haut-Rhin");
  let desc = parts.join(", ") + ". ";
  desc += "Coordonnées, horaires, avis et contact direct sur Mon Commerce 68 — l'annuaire de référence du 68.";
  return desc.slice(0, 160);
}

/** Inject all meta + OG + Twitter + canonical + hreflang for a business page. */
export function injectBusinessMeta(biz) {
  if (!biz) return;
  const url = safeWindowUrl();
  const canonical = biz.slug ? `${SITE}/entreprise/${biz.slug}` : url;
  const title = buildBusinessTitle(biz);
  const description = buildBusinessDescription(biz);
  const image = (biz.photos && biz.photos[0]) || biz.logo || `${SITE}/og-default.jpg`;

  document.title = title;
  injectMeta("description", description);
  injectMeta("keywords", buildKeywords(biz));
  injectMeta("robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
  injectMeta("geo.region", "FR-68");
  injectMeta("geo.placename", biz.city || "Haut-Rhin");
  if (biz.lat && biz.lon) injectMeta("geo.position", `${biz.lat};${biz.lon}`);
  if (biz.lat && biz.lon) injectMeta("ICBM", `${biz.lat}, ${biz.lon}`);
  injectCanonical(canonical);

  // Open Graph
  injectMeta("og:title", title, "property");
  injectMeta("og:description", description, "property");
  injectMeta("og:type", "business.business", "property");
  injectMeta("og:url", canonical, "property");
  injectMeta("og:image", image, "property");
  injectMeta("og:locale", "fr_FR", "property");
  injectMeta("og:site_name", "Mon Commerce 68", "property");
  if (biz.city) injectMeta("business:contact_data:locality", biz.city, "property");
  if (biz.postal_code) injectMeta("business:contact_data:postal_code", biz.postal_code, "property");
  injectMeta("business:contact_data:country_name", "France", "property");

  // Twitter card
  injectMeta("twitter:card", "summary_large_image");
  injectMeta("twitter:title", title);
  injectMeta("twitter:description", description);
  injectMeta("twitter:image", image);
}

/** Inject FAQPage JSON-LD for rich-result eligibility. */
export function injectFAQSchema(biz) {
  if (!biz) return;
  const city = biz.city || "Haut-Rhin";
  const cat = (biz.category || "Commerce").toLowerCase();
  const addr = biz.address || `${city}${biz.postal_code ? ` (${biz.postal_code})` : ""}`;
  const phone = biz.phone ? biz.phone : "le formulaire de contact direct sur la fiche";
  const questions = [
    {
      q: `Où se trouve ${biz.name} ?`,
      a: `${biz.name} est situé à ${addr}${biz.postal_code ? `, ${biz.postal_code}` : ""} ${city}, dans le département du Haut-Rhin (68).`,
    },
    {
      q: `Comment contacter ${biz.name} ?`,
      a: `Vous pouvez contacter ${biz.name} via ${phone}${biz.phone ? "" : " — la réponse arrive par email au commerçant"}. Toutes les coordonnées sont visibles sur cette fiche Mon Commerce 68.`,
    },
    {
      q: `Quelle est l'activité de ${biz.name} ?`,
      a: `${biz.name} est un établissement de type "${cat}" à ${city}. Découvrez son activité complète, ses horaires et les avis clients sur sa fiche Mon Commerce 68.`,
    },
    biz.rating_count > 0 ? {
      q: `Quels sont les avis sur ${biz.name} ?`,
      a: `${biz.name} a une note moyenne de ${biz.rating_avg?.toFixed(1)}/5 basée sur ${biz.rating_count} avis client${biz.rating_count > 1 ? "s" : ""} vérifiés.`,
    } : null,
    {
      q: `${biz.name} est-il ouvert aujourd'hui ?`,
      a: `Consultez les horaires d'ouverture à jour de ${biz.name} directement sur cette fiche. Les commerçants partenaires mettent leurs horaires à jour en temps réel sur Mon Commerce 68.`,
    },
  ].filter(Boolean);

  injectJsonLd("mc68-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  });

  return questions;
}

/** Minimal client-side slug matching Python slugify enough for city names. */
export function slugify(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
